<script setup lang="ts">
import { usersStore } from '@/src/store/users';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useGlobalProperties } from '@/src/composables/useGlobalProperties';
import { useKlaviyo } from '@/src/composables/useKlaviyo';
import SePopup from '@/src/ui-kit/popup/Popup.vue';
import SeIcon from '@/src/ui-kit/icon/Icon.vue';
import SeButton from '@/src/ui-kit/button/Button.vue';

const { trackSpecificEvent } = useKlaviyo();
const trackNotifyMe = trackSpecificEvent('Back Online Notification Requested');

const { getGlobalProperties } = useGlobalProperties();
const { $api } = getGlobalProperties();

const users = usersStore();
const { currentUser, notifyMeAbout } = storeToRefs(users);
const { setNotifyMeAbout } = users;

const notificationType = ref('onetime');
const loading = ref(false);
const notifyMeAboutName = computed(() => notifyMeAbout.value?.name ?? '');

const closeModal = () => {
  setNotifyMeAbout(null);
  notificationType.value = 'onetime';
};

const notifyMe = () => {
  loading.value = true;
  $api
    .post(`/api/user/${notifyMeAbout.value?.id}/notify`, { type: notificationType.value })
    .then((res: any) => {
      trackNotifyMe({
        advisor_id: notifyMeAbout.value.id,
        advisor_name: notifyMeAbout.value.name,
        advisor_categories: notifyMeAbout.value.profile_categories.map((item) => item.category.name),
        notification_type: notificationType.value,
      });
      loading.value = false;
      notificationType.value = 'onetime';
      setNotifyMeAbout(null);
      currentUser.value.notify.push(res.data);
    })
    .catch((error: any) => {
      console.error(error.response);
      loading.value = false;
      notificationType.value = 'onetime';
      setNotifyMeAbout(null);
    });
};
</script>

<script lang="ts">
export default {
  name: 'SeNotifyMePopup',
};
</script>

<template>
  <se-popup
    v-if="notifyMeAbout !== null && currentUser && currentUser.role_name === 'user'"
    classes="sm:w-[327px] py-24"
    :show-close="false"
  >
    <div class="flex flex-col gap-16 justify-center items-center text-center">
      <div class="bg-primary-10 p-20 rounded-[100px] inline-block overflow-hidden">
        <se-icon name="bell" :size="28" class="text-primary-140" />
      </div>
      <p class="text-h-m font-semibold">
        Notify me as soon as <b>{{ notifyMeAboutName }}</b> is back online
      </p>
      <div class="bg-neutral-20 p-1 rounded-[80px] w-full">
        <label class="toggle-option" :class="{ 'bg-neutral-0': notificationType === 'onetime' }">
          <input type="radio" name="question" v-model="notificationType" value="onetime" class="hidden" />
          One time
        </label>
        <label class="toggle-option" :class="{ 'bg-neutral-0': notificationType === 'everytime' }">
          <input type="radio" name="question" v-model="notificationType" value="everytime" class="hidden" />
          Everytime
        </label>
      </div>
      <p class="text-b-s">
        You will be notified {{ notificationType === 'onetime' ? 'once' : 'every time' }}, when advisor is back online.
      </p>
      <div class="w-full">
        <se-button class="w-full mb-12" @click="notifyMe"> Notify Me </se-button>
        <se-button type="tertiary" class="w-full ml-0" @click="closeModal"> Cancel </se-button>
      </div>
    </div>
  </se-popup>
</template>

<style lang="scss">
.toggle-option {
  @apply cursor-pointer rounded-[70px] py-8 w-1/2 text-b-s inline-block;
}
</style>
